import React from 'react';

import { Button } from '@mui/material';
import { ChatIcon, DocumentCopyIcon } from '@sharedCR/coreDesignSystem/icons';

import { IconSize } from 'constants/coreDesignSystem';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { LICENSE_NUMBER_MAX_LENGTH } from 'constants/loanApplication';
import { getConditionalArray } from 'utils/data';

import { TitleInstructionsFieldId } from './Content/Content.constants';
import { GROUP_TITLE_CLASS_NAME } from './EditDrawer.styles';

export const getCompanyFieldIds = prefix => ({
  attendantCompanyId: `${prefix}AttendantCompanyId`,
  attendantCompanyLocationId: `${prefix}AttendantCompanyLocationId`,
  name: `${prefix}Name`,
  licenseNumber: `${prefix}LicenseNumber`,
  escrowNumber: `${prefix}EscrowNumber`,
  streetAddress: `${prefix}StreetAddress`,
  suite: `${prefix}Suite`,
  city: `${prefix}City`,
  state: `${prefix}State`,
  zipCode: `${prefix}ZipCode`,
  closerName: `${prefix}CloserName`,
  closerContactEmail: `${prefix}CloserContactEmail`,
  closerContactPhone: `${prefix}CloserContactPhone`,
  closerLicenseNumber: `${prefix}CloserLicenseNumber`,
});

export const getAttorneyFieldIds = prefix => ({
  attendantCompanyId: `${prefix}AttendantCompanyId`,
  attendantCompanyLocationId: `${prefix}AttendantCompanyLocationId`,
  name: `${prefix}Name`,
  licenseNumber: `${prefix}LicenseNumber`,
  streetAddress: `${prefix}StreetAddress`,
  suite: `${prefix}Suite`,
  city: `${prefix}City`,
  state: `${prefix}State`,
  zipCode: `${prefix}ZipCode`,
  phone: `${prefix}Phone`,
  email: `${prefix}Email`,
  contactPerson: `${prefix}ContactPerson`,
  contactPhone: `${prefix}ContactPhone`,
  contactEmail: `${prefix}ContactEmail`,
});

const getAttorneySectionGroups = ({
  prefix,
  attorneyLabel,
  companyOptions,
  customOnChange,
  hasAttorney,
}) => {
  const fieldId = getAttorneyFieldIds(prefix);

  return [
    {
      type: FieldType.GROUP,
      key: `${prefix}-attorney-info`,
      groupTitle: {
        label: `${attorneyLabel} Information`,
        icon: <DocumentCopyIcon size={IconSize[16]} />,
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: fieldId.attendantCompanyId,
          type: FieldType.SELECT,
          nameInModel: fieldId.attendantCompanyId,
          inputProps: {
            label: 'Company Name',
            placeholder: 'Company Name',
            variant: 'outlined',
            options: companyOptions,
            widthInColumns: 1,
            customOnChange,
          },
        },
        ...getConditionalArray(hasAttorney, [
          {
            fieldId: fieldId.name,
            type: FieldType.INPUT,
            nameInModel: fieldId.name,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Attorney Name',
              placeholder: 'Attorney Name',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.licenseNumber,
            type: FieldType.INPUT,
            nameInModel: fieldId.licenseNumber,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'License Number',
              placeholder: 'License Number',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 1,
            },
          },
        ]),
      ],
    },
    ...getConditionalArray(hasAttorney, [
      {
        type: FieldType.GROUP,
        key: `${prefix}-attorney-address`,
        fields: [
          {
            fieldId: fieldId.streetAddress,
            type: FieldType.INPUT,
            nameInModel: fieldId.streetAddress,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Street Address',
              placeholder: 'Street Address',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: fieldId.suite,
            type: FieldType.INPUT,
            nameInModel: fieldId.suite,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Suite',
              placeholder: 'Suite',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.city,
            type: FieldType.INPUT,
            nameInModel: fieldId.city,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'City',
              placeholder: 'City',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.state,
            type: FieldType.INPUT,
            nameInModel: fieldId.state,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'State',
              placeholder: 'State',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.zipCode,
            type: FieldType.INPUT,
            nameInModel: fieldId.zipCode,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Zip Code',
              placeholder: 'Zip Code',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
        ],
      },
      {
        type: FieldType.GROUP,
        key: `${prefix}-attorney-contact`,
        groupTitle: {
          label: 'Contact Information',
          icon: <ChatIcon size={IconSize[16]} />,
          className: GROUP_TITLE_CLASS_NAME,
        },
        fields: [
          {
            fieldId: fieldId.phone,
            type: FieldType.TEL,
            nameInModel: fieldId.phone,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEL_EXT,
              label: 'Phone',
              placeholder: 'Phone',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.email,
            type: FieldType.INPUT,
            nameInModel: fieldId.email,
            inputProps: {
              type: FieldInputType.EMAIL,
              label: 'Email',
              placeholder: 'Email',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 1,
              required: false,
            },
          },
          {
            fieldId: fieldId.contactPerson,
            type: FieldType.INPUT,
            nameInModel: fieldId.contactPerson,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Contact Name',
              placeholder: 'Contact Name',
              variant: 'outlined',
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.contactPhone,
            type: FieldType.TEL,
            nameInModel: fieldId.contactPhone,
            inputProps: {
              type: FieldInputType.TEL_EXT,
              label: 'Contact Phone',
              placeholder: 'Contact Phone',
              variant: 'outlined',
              required: false,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.contactEmail,
            type: FieldType.INPUT,
            nameInModel: fieldId.contactEmail,
            inputProps: {
              type: FieldInputType.EMAIL,
              label: 'Contact Email',
              placeholder: 'Contact Email',
              variant: 'outlined',
              widthInColumns: 1,
              required: false,
            },
          },
        ],
      },
    ]),
  ];
};

const getCompanySectionGroups = ({
  prefix,
  companyLabel,
  companyOptions,
  customOnChange,
  hasCompany,
  onChangeLocation,
}) => {
  const fieldId = getCompanyFieldIds(prefix);

  return [
    {
      type: FieldType.GROUP,
      key: `${prefix}-company-info`,
      groupTitle: {
        label: `${companyLabel} Company Information`,
        icon: <DocumentCopyIcon size={IconSize[16]} />,
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: fieldId.attendantCompanyId,
          type: FieldType.SELECT,
          nameInModel: fieldId.attendantCompanyId,
          inputProps: {
            label: `${companyLabel} Company Name`,
            placeholder: `${companyLabel} Company Name`,
            variant: 'outlined',
            options: companyOptions,
            widthInColumns: 1,
            customOnChange,
            required: hasCompany,
          },
        },
        ...getConditionalArray(hasCompany, [
          {
            fieldId: fieldId.name,
            type: FieldType.INPUT,
            nameInModel: fieldId.name,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Provider Name',
              placeholder: 'Provider Name',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.licenseNumber,
            type: FieldType.INPUT,
            nameInModel: fieldId.licenseNumber,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'License Number',
              placeholder: 'License Number',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.escrowNumber,
            type: FieldType.INPUT,
            nameInModel: fieldId.escrowNumber,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
              label: 'Escrow Number',
              placeholder: 'Escrow Number',
              variant: 'outlined',
              widthInColumns: 1,
            },
          },
        ]),
      ],
    },
    ...getConditionalArray(hasCompany, [
      {
        type: FieldType.GROUP,
        key: `${prefix}-company-address`,
        fields: [
          {
            fieldId: fieldId.streetAddress,
            type: FieldType.INPUT,
            nameInModel: fieldId.streetAddress,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Street Address',
              placeholder: 'Street Address',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 2,
            },
          },
          {
            fieldId: fieldId.suite,
            type: FieldType.INPUT,
            nameInModel: fieldId.suite,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Suite',
              placeholder: 'Suite',
              variant: 'outlined',
              disabled: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.city,
            type: FieldType.INPUT,
            nameInModel: fieldId.city,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'City',
              placeholder: 'City',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.state,
            type: FieldType.INPUT,
            nameInModel: fieldId.state,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'State',
              placeholder: 'State',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.zipCode,
            type: FieldType.INPUT,
            nameInModel: fieldId.zipCode,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Zip Code',
              placeholder: 'Zip Code',
              variant: 'outlined',
              disabled: true,
              required: true,
              widthInColumns: 1,
            },
          },
        ],
        additionalGroupContentConfig: {
          className: 'col-span-full',
          fields: [
            {
              key: `${prefix}-change-location`,
              component: Button,
              variant: 'outlined',
              color: 'primary',
              size: 'small',
              children: 'Change Location',
              onClick: onChangeLocation,
            },
          ],
        },
      },
      {
        type: FieldType.GROUP,
        key: `${prefix}-closer-info`,
        groupTitle: {
          label: 'Closer Information',
          icon: <ChatIcon size={IconSize[16]} />,
          className: GROUP_TITLE_CLASS_NAME,
        },
        fields: [
          {
            fieldId: fieldId.closerName,
            type: FieldType.INPUT,
            nameInModel: fieldId.closerName,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Name',
              placeholder: 'Name',
              variant: 'outlined',
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.closerContactPhone,
            type: FieldType.TEL,
            nameInModel: fieldId.closerContactPhone,
            inputProps: {
              type: FieldInputType.TEL_EXT,
              label: 'Phone',
              placeholder: 'Phone',
              variant: 'outlined',
              required: false,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.closerContactEmail,
            type: FieldType.INPUT,
            nameInModel: fieldId.closerContactEmail,
            inputProps: {
              type: FieldInputType.EMAIL,
              label: 'Email',
              placeholder: 'Email',
              variant: 'outlined',
              widthInColumns: 1,
              required: false,
            },
          },
          {
            fieldId: fieldId.closerLicenseNumber,
            type: FieldType.INPUT,
            nameInModel: fieldId.closerLicenseNumber,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'License Number',
              placeholder: 'License Number',
              variant: 'outlined',
              widthInColumns: 1,
              maxLength: LICENSE_NUMBER_MAX_LENGTH,
            },
          },
        ],
      },
    ]),
  ];
};

const getSectionDivider = key => ({
  type: FieldType.GROUP,
  key,
  title: { withDivider: true },
  fields: [],
});

export const getTitleCompanyFormConfig = ({
  titleCompanyOptions,
  escrowCompanyOptions,
  onTitleCompanyChange,
  onEscrowCompanyChange,
  onChangeTitleLocation,
  onChangeEscrowLocation,
  hasTitleCompany,
  hasEscrowCompany,
  sellerAttorneyOptions,
  buyerAttorneyOptions,
  onSellerAttorneyChange,
  onBuyerAttorneyChange,
  hasSellerAttorney,
  hasBuyerAttorney,
}) => ({
  groups: [
    ...getCompanySectionGroups({
      prefix: 'title',
      companyLabel: 'Title',
      companyOptions: titleCompanyOptions,
      customOnChange: onTitleCompanyChange,
      hasCompany: hasTitleCompany,
      onChangeLocation: onChangeTitleLocation,
    }),
    getSectionDivider('titleEscrowDivider'),
    ...getCompanySectionGroups({
      prefix: 'escrow',
      companyLabel: 'Escrow',
      companyOptions: escrowCompanyOptions,
      customOnChange: onEscrowCompanyChange,
      hasCompany: hasEscrowCompany,
      onChangeLocation: onChangeEscrowLocation,
    }),
    getSectionDivider('escrowSellerAttorneyDivider'),
    ...getAttorneySectionGroups({
      prefix: 'sellerAttorney',
      attorneyLabel: "Seller's Attorney",
      companyOptions: sellerAttorneyOptions,
      customOnChange: onSellerAttorneyChange,
      hasAttorney: hasSellerAttorney,
    }),
    getSectionDivider('sellerBuyerAttorneyDivider'),
    ...getAttorneySectionGroups({
      prefix: 'buyerAttorney',
      attorneyLabel: "Buyer's Attorney",
      companyOptions: buyerAttorneyOptions,
      customOnChange: onBuyerAttorneyChange,
      hasAttorney: hasBuyerAttorney,
    }),
    getSectionDivider('buyerAttorneyTitleInstructionsDivider'),
    {
      type: FieldType.GROUP,
      key: 'title-instructions',
      groupTitle: {
        label: 'Title Instructions',
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: TitleInstructionsFieldId.SPECIAL_ENDORSEMENT,
          type: FieldType.INPUT,
          nameInModel: TitleInstructionsFieldId.SPECIAL_ENDORSEMENT,
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Special Endorsement',
            variant: 'outlined',
            required: false,
            multiline: true,
            rows: 4,
            columns: 3,
          },
        },
      ],
    },
  ],
});
